"use client";

import { useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Sparkles, ChevronDown, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const features = [{
  icon: CheckCircle2,
  title: "Inside oven",
  description: "Deep degreasing and thorough removal of baked-on food residue, restoring your oven interior to pristine condition.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/inside_oven.png",
  imageAlt: "Inside oven deep cleaning service",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/inside_oven.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/inside_oven.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Inside fridge",
  description: "Complete interior sanitization, shelf wipe-down, and drawer cleaning for a fresh and hygienic food storage space.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/inside_fridge.png",
  imageAlt: "Inside refrigerator cleaning service",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/inside_fridge.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/inside_fridge.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Interior windows",
  description: "Crystal clear glass cleaning for all interior-facing windows and sills to maximize natural light throughout your home.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/interior_windows.png",
  imageAlt: "Interior windows sparkling clean",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/interior_windows.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/interior_windows.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Laundry (per load)",
  description: "Professional washing, drying, and neat folding handled right on-site to lighten your weekly household chores.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/laundry_service.png",
  imageAlt: "Fresh laundry folding service",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/laundry_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/laundry_service.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Garage/basement",
  description: "Sweeping, dust clearing, and debris removal to transform your garage or basement into a clean, usable space.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/garage_basement.png",
  imageAlt: "Clean garage and basement area",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/garage_basement.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/garage_basement.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Pet hair treatment",
  description: "Specialized vacuuming and specialized tools designed to lift embedded pet hair from upholstery, rugs, and carpets.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/pet_hair_treatment.png",
  imageAlt: "Pet hair removal treatment on furniture",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/pet_hair_treatment.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/pet_hair_treatment.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Green/eco-product upgrade",
  description: "100% plant-based, non-toxic, allergen-free cleaning supplies utilized exclusively throughout your entire property.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/eco_product_upgrade.png",
  imageAlt: "Green eco-friendly cleaning product upgrade",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/eco_product_upgrade.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/eco_product_upgrade.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Fridge coil cleaning",
  description: "Careful dust and lint removal from refrigerator coils to improve appliance efficiency and prolong lifespan.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/fridge_coil_cleaning.png",
  imageAlt: "Refrigerator coil cleaning and maintenance",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/fridge_coil_cleaning.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/fridge_coil_cleaning.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Cabinet organization",
  description: "Systematic sorting and neat arrangement of pantry, kitchen, or bathroom cabinets for maximum accessibility.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/cabinet_organization.png",
  imageAlt: "Organized kitchen cabinets",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/cabinet_organization.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/cabinet_organization.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Wall spot-treatment",
  description: "Gentle cleaning of scuffs, fingerprints, and marks on painted walls without damaging the underlying finish.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/wall_spot_treatment.png",
  imageAlt: "Wall spot treatment and scuff removal",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/wall_spot_treatment.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/wall_spot_treatment.png-wvc-sizes"
}, {
  icon: CheckCircle2,
  title: "Baseboard heater deep clean",
  description: "Dust and trapped debris removal from baseboard heating units to improve indoor air quality and heating efficiency.",
  type: "Add-On",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/baseboard_heater_deep_clean.png",
  imageAlt: "Baseboard heater deep cleaning",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/baseboard_heater_deep_clean.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/baseboard_heater_deep_clean.png-wvc-sizes"
}];
export default function ServiceAddons() {
  const container = useRef<HTMLDivElement>(null);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduce: "(prefers-reduced-motion: reduce)",
      motion: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduce: boolean;
        motion: boolean;
      };
      if (conditions.reduce) {
        gsap.set([".addon-eyebrow", ".addon-heading", ".addon-lede", ".addon-card"], {
          opacity: 1,
          y: 0
        });
        return;
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%"
        },
        defaults: {
          ease: "power3.out",
          duration: 0.6
        }
      });
      tl.from(".addon-eyebrow", {
        opacity: 0,
        y: 20
      }).from(".addon-heading", {
        opacity: 0,
        y: 25
      }, "-=0.4").from(".addon-lede", {
        opacity: 0,
        y: 20
      }, "-=0.4").from(".addon-card", {
        opacity: 0,
        y: 35,
        stagger: 0.12
      }, "-=0.3");
    });
  }, {
    scope: container
  });
  return <section data-section-id="24667" id="serviceaddons" data-nav="dark" ref={container} className="relative bg-[hsl(220,40%,15%)] py-24 md:py-32 text-white overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1740567/img/glacial_wash_motif.png)] bg-cover bg-center opacity-5 pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-[1360px] px-5 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16">
          <div className="max-w-2xl">
            <div className="addon-eyebrow flex items-center gap-3 mb-4">
              <span className="h-3 w-[2px] bg-[var(--accent)]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]">
                Custom Enhancements
              </span>
            </div>
            <h2 className="addon-heading font-default text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white">
              Residential Add-On Services
            </h2>
            <p className="addon-lede mt-4 text-white/80 text-base md:text-lg leading-relaxed">
              Elevate your standard residential cleaning plan with targeted, meticulous add-on packages designed for thorough renewal.
            </p>
          </div>
          <div className="mt-8 md:mt-0">
            <Button asChild className="rounded-lg px-8 py-6 font-bold uppercase text-xs tracking-wider bg-[var(--accent)] text-foreground hover:bg-[var(--accent)]/90 transition-all shadow-lg">
              <Link to="/GetAQuotePage">
                Customize Your Package
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const isSelected = selectedService === i;
          return <div key={i} data-index={i} className="addon-card flex">
                <Card className="w-full flex flex-col justify-between rounded-2xl bg-[hsl(220,40%,18%)] border border-white/10 text-white overflow-hidden shadow-2xl transition-all duration-300 hover:border-[var(--accent)]/50 hover:-translate-y-1">
                  <div>
                    <div className="relative overflow-hidden aspect-[16/9]">
                      <img src={feature.image} alt={feature.imageAlt} data-wvc-srcset={feature.imageSrcset} data-wvc-sizes={feature.imageSizes} loading="lazy" className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" />
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-white/90 text-foreground font-semibold px-3 py-1 text-xs rounded-lg border-none shadow">
                          {feature.type}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="p-6 pb-2">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-[var(--accent)]/15 text-[var(--accent)]">
                          <Icon className="size-5" strokeWidth={2} />
                        </div>
                        <CardTitle className="font-default text-xl font-bold uppercase tracking-wide text-white">
                          {feature.title}
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="px-6 py-2">
                      <CardDescription className="text-white/80 text-sm md:text-base leading-relaxed">
                        {feature.description}
                      </CardDescription>

                      {isSelected && <div className="mt-4 pt-4 border-t border-white/10 text-white/90 text-sm leading-relaxed animate-fadeIn">
                          <p>
                            Delivered by our vetted network of cleaning professionals with meticulous quality control, ensuring pristine results and total satisfaction.
                          </p>
                        </div>}
                    </CardContent>
                  </div>

                  <CardFooter className="p-6 pt-4 flex flex-col gap-3 border-t border-white/5">
                    <Button variant="ghost" onClick={() => setSelectedService(isSelected ? null : i)} className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[var(--accent)] hover:text-white hover:bg-white/5 py-2 px-3 rounded-lg h-auto">
                      <span>{isSelected ? "Hide Details" : "View Add-On Specs"}</span>
                      <ChevronDown className={`size-4 transition-transform duration-300 ${isSelected ? "rotate-180" : ""}`} />
                    </Button>

                    <Button asChild className="w-full rounded-lg bg-white/10 text-white hover:bg-[var(--accent)] hover:text-foreground font-bold text-xs uppercase tracking-wider transition-all border border-white/20">
                      <Link to="/GetAQuotePage">
                        Add to Estimate
                        <ArrowRight className="ml-1 size-3.5" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}